import { lazy, Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';
import type { RedemptionModalOrderData } from '@shared/dream-components';

import { usePageContext } from '~/context/usePageContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { ToastData } from '~/hooks/useToast';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { Product } from '~/interfaces/product';
import type { RootLoaderData } from '~/root';
import { getOrCreateAnonymousUserId } from '~/utils/nectarTracking';
import { NECTAR_SITE_ID_FIELD } from '~/utils/nectarTrackingFields';
import toast from '~/utils/toast';

// Owns the @calcom/atoms import chain; lazy so Cal.com stays out of the
// products route chunk until a coaching checkout actually renders.
const CoachingBookingModal = lazy(() => import('~/components/DreamBuilder/nodes/Button/CoachingBookingModal'));

interface GuestCoachingCheckoutModalProps {
  product: Product;
  productVariantId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: { order?: RedemptionModalOrderData; toast?: ToastData }) => void;
}

const GuestCoachingCheckoutModal = ({
  product,
  productVariantId,
  isOpen,
  onClose,
  onSuccess,
}: GuestCoachingCheckoutModalProps) => {
  const { t } = useTranslation();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const { visitToken, referralId, cookies } = usePageContext();
  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);
  const fetcher = useNonRevalidatingFetcher<() => Promise<{ order?: RedemptionModalOrderData; toast?: ToastData }>>();
  const hasSubmittedRef = useRef(false);

  const redeemableMetadata = product.default_product_variant?.redeemable?.metadata as
    | { slug: string; users: { username: string }[] }
    | undefined;

  useEffect(() => {
    if (!hasSubmittedRef.current || fetcher.state !== 'idle' || fetcher.data === undefined) return;

    hasSubmittedRef.current = false;

    const data = fetcher.data as { order?: RedemptionModalOrderData; toast?: ToastData } | undefined;

    if (data?.toast?.status === 'error') {
      toast.error(data.toast.message || t('errors.default'));
      return;
    }

    if (data?.order) {
      onSuccess({ order: data.order });
    }
  }, [fetcher.state, fetcher.data, onSuccess, t]);

  const handleBookingSuccess = (bookingData: CalComBooking) => {
    const email = bookingData.attendees[0].email.toLowerCase();
    const name = bookingData.attendees[0].name;

    const formData = new FormData();

    formData.set('email', email);
    formData.set('product_variant_id', productVariantId);
    formData.set('booking_attendee_email', email);
    formData.set('booking_attendee_name', name);
    formData.set('booking_data', JSON.stringify(bookingData));
    formData.set('booking_provider', 'cal_com');
    formData.set('is_js_enabled', 'true');
    formData.set('sent_from_orchid', 'true');

    if (visitToken) {
      formData.set('visit_token', visitToken);
    }
    if (referralId) {
      formData.set('ref', referralId);
    }
    if (boostAffiliateRecommendingPublicationId) {
      formData.set('bhba', boostAffiliateRecommendingPublicationId);
    }

    if (typeof window !== 'undefined') {
      formData.set('nectar_signup_url', window.location.href);
      formData.set('nectar_anonymous_user_id', getOrCreateAnonymousUserId());
      if (site.id) formData.set(NECTAR_SITE_ID_FIELD, site.id);
    }

    hasSubmittedRef.current = true;
    fetcher.submit(formData, { method: 'post', action: '/create' });
  };

  if (!redeemableMetadata?.slug || !redeemableMetadata?.users?.[0]?.username) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <CoachingBookingModal
        isOpen={isOpen}
        onClose={onClose}
        onBookingSuccess={handleBookingSuccess}
        shouldDisableConfirmButton={fetcher.state === 'submitting'}
        eventSlug={redeemableMetadata.slug}
        calComUserName={redeemableMetadata.users[0].username}
      />
    </Suspense>
  );
};

export default GuestCoachingCheckoutModal;
